import React, { useEffect, useMemo, useState } from 'react';
import {
    Button,
    Card,
    Col,
    DatePicker,
    Descriptions,
    Form,
    Grid,
    Input,
    InputNumber,
    message,
    Modal,
    Row,
    Select,
    Space,
    Table,
    Tag,
    Typography,
} from 'antd';
import {
    DeleteOutlined,
    EyeOutlined,
    PlusOutlined,
    ReloadOutlined,
    RetweetOutlined,
    SwapOutlined,
    UndoOutlined,
} from '@ant-design/icons';
import client from '../api/client';
import dayjs from 'dayjs';
import { useAuth } from '../context/AuthContext';

const { Title } = Typography;

const StockTransfersPage = () => {
    const screens = Grid.useBreakpoint();
    const isMobile = !screens.md;
    const { user, hasPermission } = useAuth();
    const [form] = Form.useForm();
    const [transfers, setTransfers] = useState([]);
    const [branches, setBranches] = useState([]);
    const [locations, setLocations] = useState([]);
    const [items, setItems] = useState([]);
    const [lines, setLines] = useState([]);
    const [lineItemId, setLineItemId] = useState(null);
    const [lineQuantity, setLineQuantity] = useState(null);
    const [lineAvailable, setLineAvailable] = useState(null);
    const [loading, setLoading] = useState(false);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [availableLoading, setAvailableLoading] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [detailOpen, setDetailOpen] = useState(false);
    const [detailLoading, setDetailLoading] = useState(false);
    const [selectedTransfer, setSelectedTransfer] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [dates, setDates] = useState([dayjs().startOf('month'), dayjs().endOf('month')]);

    const sourceBranchId = Form.useWatch('source_branch_id', form);
    const sourceLocationId = Form.useWatch('source_location_id', form);
    const destinationBranchId = Form.useWatch('destination_branch_id', form);

    const fetchTransfers = async () => {
        setLoading(true);
        try {
            const params = {};
            if (dates) {
                params.start_date = dates[0].format('YYYY-MM-DD');
                params.end_date = dates[1].format('YYYY-MM-DD');
            }
            const { data } = await client.get('/inventory/transfers', { params });
            setTransfers(data);
        } catch (error) {
            message.error('Failed to fetch stock transfers');
        } finally {
            setLoading(false);
        }
    };

    const fetchMasterData = async () => {
        try {
            const [branchesRes, locationsRes, itemsRes] = await Promise.all([
                client.get('/master/branches'),
                client.get('/master/locations'),
                client.get('/master/items'),
            ]);
            setBranches(branchesRes.data);
            setLocations(locationsRes.data);
            setItems(itemsRes.data.filter((item) => item.is_active !== false));
        } catch (error) {
            message.error('Failed to load transfer form data');
        }
    };

    useEffect(() => {
        fetchMasterData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        fetchTransfers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dates]);

    useEffect(() => {
        const fetchAvailable = async () => {
            if (!sourceBranchId || !sourceLocationId || !lineItemId) {
                setLineAvailable(null);
                return;
            }

            setAvailableLoading(true);
            try {
                const { data } = await client.get('/inventory/ledger', {
                    params: {
                        item_id: lineItemId,
                        branch_id: sourceBranchId,
                        location_id: sourceLocationId,
                    },
                });
                const available = data.reduce((sum, entry) => sum + Number(entry.quantity || 0), 0);
                setLineAvailable(available);
            } catch (error) {
                setLineAvailable(null);
            } finally {
                setAvailableLoading(false);
            }
        };

        fetchAvailable();
    }, [sourceBranchId, sourceLocationId, lineItemId]);

    const sourceLocations = useMemo(() => {
        if (!sourceBranchId) return [];
        return locations.filter((location) => Number(location.branch_id) === Number(sourceBranchId));
    }, [locations, sourceBranchId]);

    const destinationLocations = useMemo(() => {
        if (!destinationBranchId) return [];
        return locations.filter((location) => Number(location.branch_id) === Number(destinationBranchId));
    }, [locations, destinationBranchId]);

    const filteredTransfers = useMemo(() => {
        const term = searchTerm.trim().toLowerCase();
        if (!term) return transfers;
        return transfers.filter((transfer) => (
            (transfer.transfer_number || '').toLowerCase().includes(term) ||
            (transfer.source_branch?.name || '').toLowerCase().includes(term) ||
            (transfer.destination_branch?.name || '').toLowerCase().includes(term) ||
            (transfer.status || '').toLowerCase().includes(term)
        ));
    }, [transfers, searchTerm]);

    const stats = useMemo(() => ({
        total: filteredTransfers.length,
        posted: filteredTransfers.filter((transfer) => transfer.status === 'POSTED').length,
        reversed: filteredTransfers.filter((transfer) => transfer.status === 'REVERSED').length,
    }), [filteredTransfers]);

    const totalQuantity = useMemo(() => (
        lines.reduce((sum, line) => sum + Number(line.quantity || 0), 0)
    ), [lines]);
    const canReverseTransfer = user?.role === 'ADMIN' || user?.role === 'MANAGER' || hasPermission('void_transaction');

    const resetCreateForm = () => {
        form.resetFields();
        form.setFieldsValue({ transfer_date: dayjs() });
        setLines([]);
        setLineItemId(null);
        setLineQuantity(null);
        setLineAvailable(null);
    };

    const handleAddLine = () => {
        if (!sourceBranchId || !sourceLocationId) {
            message.error('Select a source branch and location first.');
            return;
        }
        if (!lineItemId || !lineQuantity || Number(lineQuantity) <= 0) {
            message.error('Select an item and enter a valid quantity.');
            return;
        }
        if (lines.some((line) => line.item_id === lineItemId)) {
            message.error('This item is already in the transfer list.');
            return;
        }
        if (lineAvailable !== null && Number(lineQuantity) > Number(lineAvailable)) {
            message.error(`Insufficient stock. Available: ${Number(lineAvailable).toLocaleString()}`);
            return;
        }

        const item = items.find((record) => record.id === lineItemId);
        setLines((current) => [
            ...current,
            {
                key: `${lineItemId}-${Date.now()}`,
                item_id: lineItemId,
                item_name: item ? `${item.name} (${item.code})` : 'Unknown item',
                unit: item?.unit?.abbreviation || '',
                quantity: Number(lineQuantity),
                available: lineAvailable,
            },
        ]);
        setLineItemId(null);
        setLineQuantity(null);
        setLineAvailable(null);
    };

    const handleSubmit = async (values) => {
        if (lines.length === 0) {
            message.error('Add at least one item to transfer.');
            return;
        }

        setSubmitLoading(true);
        try {
            await client.post('/inventory/transfers', {
                ...values,
                transfer_number: values.transfer_number?.trim() || undefined,
                transfer_date: values.transfer_date.format('YYYY-MM-DD'),
                items: lines.map((line) => ({
                    item_id: line.item_id,
                    quantity: line.quantity,
                })),
            });
            message.success('Stock transfer posted successfully');
            setModalOpen(false);
            resetCreateForm();
            fetchTransfers();
        } catch (error) {
            const validation = error.response?.data?.errors;
            const firstValidationError = validation && Object.values(validation)[0]?.[0];
            message.error(firstValidationError || error.response?.data?.message || 'Failed to post stock transfer');
        } finally {
            setSubmitLoading(false);
        }
    };

    const handleView = async (id) => {
        setDetailLoading(true);
        setSelectedTransfer(null);
        setDetailOpen(true);
        try {
            const { data } = await client.get(`/inventory/transfers/${id}`);
            setSelectedTransfer(data);
        } catch (error) {
            message.error('Failed to load transfer details');
            setDetailOpen(false);
        } finally {
            setDetailLoading(false);
        }
    };

    const handleReverse = (transfer) => {
        Modal.confirm({
            title: `Reverse ${transfer.transfer_number}?`,
            content: 'This will post opposite inventory ledger entries and mark the transfer as reversed.',
            okText: 'Reverse Transfer',
            okButtonProps: { danger: true },
            onOk: async () => {
                try {
                    await client.post(`/inventory/transfers/${transfer.id}/reverse`);
                    message.success('Stock transfer reversed');
                    fetchTransfers();
                } catch (error) {
                    const validation = error.response?.data?.errors;
                    const firstValidationError = validation && Object.values(validation)[0]?.[0];
                    message.error(firstValidationError || error.response?.data?.message || 'Failed to reverse transfer');
                }
            },
        });
    };

    const lineColumns = [
        { title: 'Item', dataIndex: 'item_name', key: 'item_name' },
        {
            title: 'Qty',
            dataIndex: 'quantity',
            key: 'quantity',
            align: 'right',
            render: (value, record) => `${Number(value).toLocaleString()} ${record.unit}`,
        },
        {
            title: 'Available',
            dataIndex: 'available',
            key: 'available',
            align: 'right',
            render: (value, record) => value === null || value === undefined
                ? 'N/A'
                : `${Number(value).toLocaleString()} ${record.unit}`,
        },
        {
            title: '',
            key: 'action',
            align: 'right',
            render: (_, record) => (
                <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => setLines((current) => current.filter((line) => line.key !== record.key))}
                />
            ),
        },
    ];

    const transferColumns = [
        {
            title: 'Transfer #',
            dataIndex: 'transfer_number',
            key: 'transfer_number',
            render: (value) => <Typography.Text strong style={{ color: '#1677ff' }}>{value}</Typography.Text>,
        },
        {
            title: 'Date',
            dataIndex: 'transfer_date',
            key: 'transfer_date',
            render: (value) => dayjs(value).format('MMM D, YYYY'),
        },
        {
            title: 'Source',
            key: 'source',
            render: (_, record) => (
                <div>
                    <Typography.Text strong>{record.source_branch?.name}</Typography.Text>
                    <br />
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>{record.source_location?.name}</Typography.Text>
                </div>
            ),
        },
        {
            title: 'Destination',
            key: 'destination',
            render: (_, record) => (
                <div>
                    <Typography.Text strong>{record.destination_branch?.name}</Typography.Text>
                    <br />
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>{record.destination_location?.name}</Typography.Text>
                </div>
            ),
        },
        {
            title: 'Qty',
            key: 'quantity',
            align: 'right',
            render: (_, record) => Number((record.items || []).reduce((sum, line) => sum + Number(line.quantity || 0), 0)).toLocaleString(),
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status) => <Tag color={status === 'REVERSED' ? 'default' : 'success'}>{status}</Tag>,
        },
        {
            title: 'Actions',
            key: 'actions',
            align: 'right',
            render: (_, record) => (
                <Space>
                    <Button size="small" icon={<EyeOutlined />} onClick={() => handleView(record.id)}>
                        View
                    </Button>
                    {record.status !== 'REVERSED' && canReverseTransfer && (
                        <Button size="small" danger icon={<UndoOutlined />} onClick={() => handleReverse(record)}>
                            Reverse
                        </Button>
                    )}
                </Space>
            ),
        },
    ];

    const detailItems = selectedTransfer?.items || [];

    const StatCard = ({ title, value, icon, color, subText }) => (
        <Card className="premium-card" bodyStyle={{ padding: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                    <Typography.Text style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', display: 'block', marginBottom: 8, textTransform: 'uppercase' }}>{title}</Typography.Text>
                    <Title level={3} style={{ margin: 0, fontWeight: 600, color: 'var(--text-primary)' }}>{value}</Title>
                    {subText && <Typography.Text style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4, display: 'block' }}>{subText}</Typography.Text>}
                </div>
                <div className="stat-icon" style={{ backgroundColor: `${color}15`, color }}>
                    {icon}
                </div>
            </div>
        </Card>
    );

    return (
        <div style={{ padding: isMobile ? '16px' : '24px 32px' }}>
            <div style={{ marginBottom: 32, display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 12 : 0, justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'flex-end' }}>
                <div>
                    <Title level={2} style={{ margin: 0, fontWeight: 800, color: 'var(--text-primary)' }}>Stock Transfers</Title>
                    <Typography.Text style={{ color: 'var(--text-secondary)', fontSize: 16 }}>Move inventory directly between branches and locations.</Typography.Text>
                </div>
                <Space wrap style={{ justifyContent: isMobile ? 'flex-start' : 'flex-end', width: isMobile ? '100%' : 'auto' }}>
                    <RangePickerSafe value={dates} onChange={setDates} isMobile={isMobile} />
                    <Button icon={<ReloadOutlined />} onClick={fetchTransfers} style={{ borderRadius: 8, width: isMobile ? '100%' : 'auto' }}>
                        Refresh
                    </Button>
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => {
                            resetCreateForm();
                            setModalOpen(true);
                        }}
                        style={{ borderRadius: 8, fontWeight: 600, width: isMobile ? '100%' : 'auto' }}
                    >
                        New Transfer
                    </Button>
                </Space>
            </div>

            <Row gutter={[24, 24]} style={{ marginBottom: 32 }}>
                <Col xs={24} sm={8}>
                    <StatCard title="Transfers" value={stats.total} icon={<SwapOutlined />} color="#1677ff" subText="Selected period" />
                </Col>
                <Col xs={24} sm={8}>
                    <StatCard title="Posted" value={stats.posted} icon={<RetweetOutlined />} color="#10b981" subText="Active movements" />
                </Col>
                <Col xs={24} sm={8}>
                    <StatCard title="Reversed" value={stats.reversed} icon={<UndoOutlined />} color="#64748b" subText="Corrected entries" />
                </Col>
            </Row>

            <Card className="premium-card" bodyStyle={{ padding: 0 }}>
                <div style={{ padding: 16, display: 'flex', justifyContent: 'flex-end' }}>
                    <Input
                        allowClear
                        placeholder="Search transfer #, branch, status..."
                        value={searchTerm}
                        onChange={(event) => setSearchTerm(event.target.value)}
                        style={{ width: isMobile ? '100%' : 340, borderRadius: 10, height: 40 }}
                    />
                </div>
                <Table
                    columns={transferColumns}
                    dataSource={filteredTransfers}
                    rowKey="id"
                    loading={loading}
                    size={isMobile ? 'small' : 'middle'}
                    scroll={isMobile ? { x: 1000 } : undefined}
                    pagination={{ defaultPageSize: 12, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'] }}
                />
            </Card>

            <Modal
                title="New Stock Transfer"
                open={modalOpen}
                onCancel={() => setModalOpen(false)}
                onOk={() => form.submit()}
                confirmLoading={submitLoading}
                okText="Post Transfer"
                width={860}
                destroyOnClose
            >
                <Form form={form} layout="vertical" onFinish={handleSubmit} initialValues={{ transfer_date: dayjs() }}>
                    <Row gutter={16}>
                        <Col xs={24} md={12}>
                            <Form.Item name="transfer_number" label="Transfer # (optional)">
                                <Input placeholder="Auto-generated when blank" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item name="transfer_date" label="Transfer Date" rules={[{ required: true }]}>
                                <DatePicker style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col xs={24} md={12}>
                            <Form.Item name="source_branch_id" label="Source Branch" rules={[{ required: true }]}>
                                <Select
                                    placeholder="Select source branch"
                                    options={branches.map((branch) => ({ label: branch.name, value: branch.id }))}
                                    onChange={() => {
                                        form.setFieldsValue({ source_location_id: undefined });
                                        setLines([]);
                                        setLineItemId(null);
                                    }}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item name="source_location_id" label="Source Location" rules={[{ required: true }]}>
                                <Select
                                    placeholder="Select source location"
                                    options={sourceLocations.map((location) => ({ label: location.name, value: location.id }))}
                                    onChange={() => {
                                        setLines([]);
                                        setLineItemId(null);
                                    }}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col xs={24} md={12}>
                            <Form.Item name="destination_branch_id" label="Destination Branch" rules={[{ required: true }]}>
                                <Select
                                    placeholder="Select destination branch"
                                    options={branches.map((branch) => ({ label: branch.name, value: branch.id }))}
                                    onChange={() => form.setFieldsValue({ destination_location_id: undefined })}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item name="destination_location_id" label="Destination Location" rules={[{ required: true }]}>
                                <Select
                                    placeholder="Select destination location"
                                    options={destinationLocations.map((location) => ({ label: location.name, value: location.id }))}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Card size="small" title="Transfer Items" style={{ marginBottom: 16 }}>
                        <Row gutter={12} align="bottom">
                            <Col xs={24} md={11}>
                                <Typography.Text strong>Item</Typography.Text>
                                <Select
                                    showSearch
                                    optionFilterProp="label"
                                    placeholder="Select item"
                                    value={lineItemId}
                                    onChange={setLineItemId}
                                    style={{ width: '100%', marginTop: 6 }}
                                    options={items.map((item) => ({
                                        label: `${item.name} (${item.code})`,
                                        value: item.id,
                                    }))}
                                />
                            </Col>
                            <Col xs={24} md={5}>
                                <Typography.Text strong>Quantity</Typography.Text>
                                <InputNumber
                                    min={0.0001}
                                    precision={4}
                                    value={lineQuantity}
                                    onChange={setLineQuantity}
                                    style={{ width: '100%', marginTop: 6 }}
                                />
                            </Col>
                            <Col xs={24} md={4}>
                                <Typography.Text strong>Available</Typography.Text>
                                <div style={{ marginTop: 10, minHeight: 32 }}>
                                    {availableLoading ? 'Loading...' : lineAvailable === null ? 'N/A' : Number(lineAvailable).toLocaleString()}
                                </div>
                            </Col>
                            <Col xs={24} md={4}>
                                <Button type="primary" icon={<PlusOutlined />} onClick={handleAddLine} block>
                                    Add
                                </Button>
                            </Col>
                        </Row>

                        <Table
                            columns={lineColumns}
                            dataSource={lines}
                            rowKey="key"
                            pagination={false}
                            size="small"
                            style={{ marginTop: 16 }}
                            scroll={isMobile ? { x: 700 } : undefined}
                        />
                    </Card>

                    <Row gutter={16}>
                        <Col xs={24} md={12}>
                            <Typography.Text type="secondary">Total transfer quantity</Typography.Text>
                            <Title level={4} style={{ marginTop: 4 }}>{totalQuantity.toLocaleString()}</Title>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item name="remarks" label="Remarks">
                                <Input.TextArea rows={2} placeholder="Optional notes" />
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Modal>

            <Modal
                title="Stock Transfer Details"
                open={detailOpen}
                onCancel={() => setDetailOpen(false)}
                footer={null}
                width={760}
            >
                {detailLoading ? (
                    <Typography.Text>Loading...</Typography.Text>
                ) : selectedTransfer ? (
                    <>
                        <Descriptions column={2} size="small">
                            <Descriptions.Item label="Transfer #">{selectedTransfer.transfer_number}</Descriptions.Item>
                            <Descriptions.Item label="Status">{selectedTransfer.status}</Descriptions.Item>
                            <Descriptions.Item label="Date">{dayjs(selectedTransfer.transfer_date).format('MMM D, YYYY')}</Descriptions.Item>
                            <Descriptions.Item label="Created By">{selectedTransfer.creator?.name || 'N/A'}</Descriptions.Item>
                            <Descriptions.Item label="Source">{selectedTransfer.source_branch?.name} / {selectedTransfer.source_location?.name}</Descriptions.Item>
                            <Descriptions.Item label="Destination">{selectedTransfer.destination_branch?.name} / {selectedTransfer.destination_location?.name}</Descriptions.Item>
                            <Descriptions.Item label="Remarks" span={2}>{selectedTransfer.remarks || 'N/A'}</Descriptions.Item>
                        </Descriptions>
                        <Table
                            dataSource={detailItems}
                            rowKey="id"
                            size="small"
                            pagination={false}
                            style={{ marginTop: 16 }}
                            columns={[
                                {
                                    title: 'Item',
                                    key: 'item',
                                    render: (_, record) => record.item ? `${record.item.name} (${record.item.code})` : 'N/A',
                                },
                                {
                                    title: 'Quantity',
                                    dataIndex: 'quantity',
                                    key: 'quantity',
                                    align: 'right',
                                    render: (value, record) => `${Number(value).toLocaleString()} ${record.item?.unit?.abbreviation || ''}`,
                                },
                            ]}
                        />
                    </>
                ) : null}
            </Modal>
        </div>
    );
};

const RangePickerSafe = ({ value, onChange, isMobile }) => {
    const { RangePicker } = DatePicker;
    return (
        <RangePicker
            value={value}
            onChange={onChange}
            style={{ borderRadius: 8, width: isMobile ? '100%' : undefined }}
        />
    );
};

export default StockTransfersPage;
